import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, AlertTriangle, CheckCircle, MinusCircle, HelpCircle, ChevronRight, ChevronLeft, Monitor } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { WaterRestrictionPolicyCard } from './WaterRestrictionPolicyCard';
import { formatTimeOfDay } from '../lib/timeFormat';
import {
  DAY_LABELS,
  type DayAbbr,
  type CityScheduleEntry,
  type ComplianceStatus,
  type ViolationKind,
  type WaterRule,
  type ProgramLike,
  type Violation,
  evaluatePropertyCompliance,
  resolvePropertyRule,
  findScheduleViolations,
  readStartTimes,
  parseStreetNumber,
} from '../lib/waterRestrictions';

interface CityProperty {
  id: string;
  name: string;
  address: string | null;
  water_restriction_class: 'residential' | 'non_residential' | null;
  water_restriction_override: WaterRule | null;
  controllers: Array<{
    id: string;
    controller_number: number;
    description: string | null;
    location: string | null;
    model: string | null;
    water_start_times: { programs?: ProgramLike[] } | null;
    water_restriction_override: { acknowledged?: boolean } | null;
  }>;
}

interface EvaluatedProperty extends CityProperty {
  status: ComplianceStatus;
  overridden: boolean;
  violationCount: number;
  controllerCount: number;
  kinds: Record<ViolationKind, number>;
  ctrl: { total: number; withSchedule: number; compliant: number; violating: number };
}

type FilterKey = 'all' | ComplianceStatus;

const STATUS_META: Record<ComplianceStatus, { label: string; color: string; chip: string; Icon: typeof CheckCircle }> = {
  compliant: { label: 'Compliant', color: '#16a34a', chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300', Icon: CheckCircle },
  non_compliant: { label: 'Non-compliant', color: '#dc2626', chip: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300', Icon: AlertTriangle },
  undetermined: { label: "Can't verify days", color: '#f59e0b', chip: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300', Icon: HelpCircle },
  no_schedule: { label: 'No schedule', color: '#9ca3af', chip: 'bg-subtle text-theme-secondary', Icon: MinusCircle },
};
const SLICE_ORDER: ComplianceStatus[] = ['compliant', 'non_compliant', 'undetermined', 'no_schedule'];

function Donut({ counts, total }: { counts: Record<ComplianceStatus, number>; total: number }) {
  const r = 54;
  const C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 120 120" className="h-32 w-32 shrink-0">
      <circle cx="60" cy="60" r={r} fill="none" stroke="var(--color-border)" strokeWidth="16" />
      {total > 0 &&
        SLICE_ORDER.map((s) => {
          const val = counts[s];
          if (val === 0) return null;
          const len = (val / total) * C;
          const el = (
            <circle
              key={s}
              cx="60"
              cy="60"
              r={r}
              fill="none"
              stroke={STATUS_META[s].color}
              strokeWidth="16"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 60 60)"
            />
          );
          offset += len;
          return el;
        })}
      <text x="60" y="56" textAnchor="middle" className="text-[20px] font-bold" style={{ fill: 'var(--color-text-primary)' }}>
        {total}
      </text>
      <text x="60" y="74" textAnchor="middle" className="text-[9px]" style={{ fill: 'var(--color-text-secondary)' }}>
        properties
      </text>
    </svg>
  );
}

function fmtViolation(v: Violation): string {
  const days = (a?: DayAbbr[]) => (a ?? []).map((d) => DAY_LABELS[d]).join(', ');
  if (v.kind === 'day') return `Waters ${days(v.days)} — not allowed (allowed: ${days(v.allowed) || 'none'})`;
  if (v.kind === 'time') return `Runs during the no-watering window (${v.window})`;
  return `Waters ${v.count} days/week — max allowed is ${v.max}`;
}

function PropertyDetail({
  property,
  entry,
  onBack,
}: {
  property: EvaluatedProperty;
  entry: CityScheduleEntry;
  onBack: () => void;
}) {
  const { i18n } = useTranslation();
  const rule = resolvePropertyRule({
    city: entry.city,
    zip: null,
    state: null,
    property_class: property.water_restriction_class,
    override: property.water_restriction_override,
    address: property.address,
    schedule: entry.schedule,
  });
  const streetNumber = parseStreetNumber(property.address);
  const meta = STATUS_META[property.status];

  return (
    <div className="p-5">
      <button onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
        <ChevronLeft size={16} /> Back to {entry.city}
      </button>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-bold text-theme-primary">{property.name}</h3>
          {property.address && <p className="text-xs text-theme-secondary">{property.address}</p>}
        </div>
        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.chip}`}>
          <meta.Icon size={12} /> {meta.label}
        </span>
      </div>

      <div className="mt-4">
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-secondary">Restriction policy</h4>
        <WaterRestrictionPolicyCard jobSiteId={property.id} />
      </div>

      <div className="mt-5">
        <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-theme-secondary">Device configuration</h4>
        {property.controllers.length === 0 ? (
          <p className="text-sm text-theme-secondary">No controllers configured for this property.</p>
        ) : (
          <div className="space-y-3">
            {property.controllers.map((c) => {
              const programs = c.water_start_times?.programs ?? [];
              const violations = findScheduleViolations(programs, rule, streetNumber);
              const overridden = violations.length > 0 && c.water_restriction_override?.acknowledged;
              return (
                <div key={c.id} className="rounded-lg border border-line p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-theme-primary">
                      <Monitor size={14} /> Controller {c.controller_number}
                      {c.description && <span className="font-normal text-theme-secondary">— {c.description}</span>}
                    </div>
                    {programs.length === 0 ? null : violations.length === 0 ? (
                      <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300">OK</span>
                    ) : overridden ? (
                      <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">Overridden</span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-500/20 dark:text-red-300">Violation</span>
                    )}
                  </div>
                  {(c.location || c.model) && (
                    <p className="mt-0.5 text-xs text-theme-secondary">{[c.location, c.model].filter(Boolean).join(' · ')}</p>
                  )}
                  {programs.length === 0 ? (
                    <p className="mt-2 text-xs text-theme-secondary">No water schedule programmed.</p>
                  ) : (
                    <ul className="mt-2 space-y-1 text-xs text-theme-secondary">
                      {programs.map((p, i) => (
                        <li key={i}>
                          <span className="font-medium text-theme-primary">Program {p.program}</span>:{' '}
                          {(p.days ?? []).map((d) => DAY_LABELS[d as DayAbbr] ?? d).join(', ') || '—'}
                          {readStartTimes(p).length > 0 ? ` at ${readStartTimes(p).map((st) => formatTimeOfDay(st, i18n.language)).join(', ')}` : ''}
                          {p.duration != null ? ` for ${p.duration} min` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {violations.length > 0 && (
                    <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-red-700 dark:text-red-300">
                      {violations.map((v, i) => (
                        <li key={i}>{fmtViolation(v)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function WaterRestrictionCityDrawer({ entry, onClose }: { entry: CityScheduleEntry; onClose: () => void }) {
  const [rows, setRows] = useState<CityProperty[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selected, setSelected] = useState<EvaluatedProperty | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiClient
      .get<CityProperty[]>(`/api/water-restriction-schedules/city-properties/${encodeURIComponent(entry.city)}`)
      .then((r) => active && setRows(r))
      .catch((err) => {
        console.error('Failed to load city properties:', err);
        if (active) setRows([]);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [entry.city]);

  const evaluated: EvaluatedProperty[] = useMemo(() => {
    if (!rows) return [];
    return rows.map((p) => {
      const r = evaluatePropertyCompliance(entry.schedule, p);
      return {
        ...p,
        status: r.status,
        overridden: r.overridden,
        violationCount: r.violationCount,
        controllerCount: p.controllers.length,
        kinds: r.kinds,
        ctrl: r.controllers,
      };
    });
  }, [rows, entry.schedule]);

  const counts = useMemo(() => {
    const c: Record<ComplianceStatus, number> = { compliant: 0, non_compliant: 0, undetermined: 0, no_schedule: 0 };
    for (const p of evaluated) c[p.status] += 1;
    return c;
  }, [evaluated]);

  // Aggregate metrics: controller rollup, overrides, coverage, violation-type tally.
  const stats = useMemo(() => {
    let ctrlCompliant = 0;
    let ctrlWithSchedule = 0;
    let overrides = 0;
    let evaluatedProps = 0;
    const kinds: Record<ViolationKind, number> = { day: 0, time: 0, max_days: 0 };
    for (const p of evaluated) {
      ctrlCompliant += p.ctrl.compliant;
      ctrlWithSchedule += p.ctrl.withSchedule;
      if (p.overridden) overrides += 1;
      if (p.status !== 'no_schedule') evaluatedProps += 1;
      kinds.day += p.kinds.day;
      kinds.time += p.kinds.time;
      kinds.max_days += p.kinds.max_days;
    }
    return { ctrlCompliant, ctrlWithSchedule, overrides, evaluatedProps, kinds };
  }, [evaluated]);

  const filtered = evaluated
    .filter((p) => (filter === 'all' ? true : p.status === filter))
    .filter((p) => p.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const total = evaluated.length;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative h-full w-full max-w-md overflow-y-auto bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-surface px-5 py-3">
          <div>
            <h2 className="text-base font-bold text-theme-primary">{entry.city} — Compliance</h2>
            <p className="text-xs text-theme-secondary">{entry.property_count} properties on this city policy</p>
          </div>
          <button onClick={onClose} className="rounded p-1.5 text-theme-secondary hover:bg-subtle" title="Close">
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-theme-secondary">Loading…</div>
        ) : selected ? (
          <PropertyDetail property={selected} entry={entry} onBack={() => setSelected(null)} />
        ) : (
          <div className="p-5">
            {!entry.schedule && (
              <div className="mb-4 flex items-start gap-1.5 rounded-md bg-amber-100 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>No water restriction policy is configured for this city, so compliance can&apos;t be evaluated.</span>
              </div>
            )}

            {/* Chart + legend */}
            <div className="flex items-center gap-5">
              <Donut counts={counts} total={total} />
              <div className="flex-1 space-y-1.5">
                {SLICE_ORDER.map((s) => {
                  const pct = total > 0 ? Math.round((counts[s] / total) * 100) : 0;
                  return (
                    <button
                      key={s}
                      onClick={() => setFilter(filter === s ? 'all' : s)}
                      className={`flex w-full items-center justify-between rounded-md px-2 py-1 text-sm text-theme-primary ${
                        filter === s ? 'bg-subtle' : 'hover:bg-subtle'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="inline-block h-3 w-3 rounded-sm" style={{ background: STATUS_META[s].color }} />
                        {STATUS_META[s].label}
                      </span>
                      <span className="font-semibold text-theme-primary">
                        {counts[s]} <span className="text-xs font-normal text-theme-secondary">({pct}%)</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Metrics */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-subtle px-2 py-2">
                <div className="text-sm font-bold text-theme-primary">{stats.evaluatedProps}/{total}</div>
                <div className="text-[10px] uppercase tracking-wide text-theme-secondary">Evaluated</div>
              </div>
              <div className="rounded-lg bg-subtle px-2 py-2">
                <div className="text-sm font-bold text-theme-primary">
                  {stats.ctrlWithSchedule > 0 ? `${stats.ctrlCompliant}/${stats.ctrlWithSchedule}` : '—'}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-theme-secondary">Controllers OK</div>
              </div>
              <div className="rounded-lg bg-subtle px-2 py-2">
                <div className="text-sm font-bold text-theme-primary">{stats.overrides}</div>
                <div className="text-[10px] uppercase tracking-wide text-theme-secondary">Overrides</div>
              </div>
            </div>

            {stats.kinds.day + stats.kinds.time + stats.kinds.max_days > 0 && (
              <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 dark:border-red-500/30 dark:bg-red-500/10">
                <div className="text-xs font-semibold text-red-800 dark:text-red-300">Violations by type</div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-red-700 dark:text-red-300">
                  <span>Wrong day: <b>{stats.kinds.day}</b></span>
                  <span>No-water hours: <b>{stats.kinds.time}</b></span>
                  <span>Too many days/wk: <b>{stats.kinds.max_days}</b></span>
                </div>
              </div>
            )}

            {counts.undetermined > 0 && (
              <div className="mt-3 flex items-start gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                <HelpCircle size={13} className="mt-0.5 shrink-0" />
                <span>
                  &ldquo;Can&apos;t verify days&rdquo; = this city assigns watering days by zone/trash day (not by address), so
                  day compliance can&apos;t be checked automatically — only the no-water hours and the days-per-week limit were verified.
                </span>
              </div>
            )}

            {/* Filters */}
            <div className="mt-5 space-y-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-secondary" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search properties…"
                  className="w-full rounded-lg border border-line bg-surface text-theme-primary py-2 pl-8 pr-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(['all', 'compliant', 'non_compliant', 'undetermined', 'no_schedule'] as FilterKey[]).map((k) => (
                  <button
                    key={k}
                    onClick={() => setFilter(k)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                      filter === k ? 'bg-blue-600 text-white' : 'bg-subtle text-theme-secondary hover:bg-subtle'
                    }`}
                  >
                    {k === 'all' ? 'All' : STATUS_META[k as ComplianceStatus].label}
                  </button>
                ))}
              </div>
            </div>

            {/* List */}
            <div className="mt-4 divide-line divide-y rounded-lg border border-line">
              {filtered.length === 0 ? (
                <div className="p-4 text-sm text-theme-secondary">No properties match.</div>
              ) : (
                filtered.map((p) => {
                  const meta = STATUS_META[p.status];
                  return (
                    <button
                      key={p.id}
                      onClick={() => setSelected(p)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-subtle"
                      title="View policy + device configuration"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-theme-primary">{p.name}</div>
                        {p.address && <div className="truncate text-xs text-theme-secondary">{p.address}</div>}
                        <div className="mt-0.5 text-xs text-theme-secondary">
                          {p.controllerCount} controller{p.controllerCount === 1 ? '' : 's'}
                          {p.status === 'non_compliant' && p.violationCount > 0 && ` · ${p.violationCount} violation${p.violationCount === 1 ? '' : 's'}`}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="flex flex-col items-end gap-1">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.chip}`}>
                            <meta.Icon size={12} /> {meta.label}
                          </span>
                          {p.overridden && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">Overridden</span>
                          )}
                        </div>
                        <ChevronRight size={16} className="text-theme-secondary" />
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
